import { Building2, Check, ChevronDown, UserCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface WorkspaceItem {
  id: number;
  label: string;
  role: string | null;
  tier: string | null;
}

interface WorkspaceSwitchProps {
  workspaces: WorkspaceItem[];
  activeId: number;
  onSelect: (workspace: WorkspaceItem) => void;
  className?: string;
}

export function WorkspaceSwitch({
  workspaces,
  activeId,
  onSelect,
  className,
}: WorkspaceSwitchProps) {
  const activeWorkspace = workspaces.find((ws) => ws.id === activeId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border border-border/50 bg-background/50 px-3 py-2 text-sm font-medium hover:bg-accent/50 hover:text-accent-foreground transition-all duration-200 text-left outline-none',
            className
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {activeWorkspace?.label?.toLowerCase().includes('personal') ? (
              <UserCircle className="h-4 w-4" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
          </div>
          <div className="flex flex-col flex-1 truncate">
            <span className="font-semibold text-xs leading-none mb-1 text-foreground">
              {activeWorkspace?.label || 'Select Workspace'}
            </span>
            <span className="text-[10px] text-muted-foreground leading-none capitalize">
              {activeWorkspace?.role || 'member'} &bull; {activeWorkspace?.tier || 'free'}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start" side="bottom" sideOffset={8}>
        {workspaces.map((ws) => {
          const isSelected = ws.id === activeId;
          return (
            <DropdownMenuItem
              key={ws.id}
              onClick={() => onSelect(ws)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer rounded-md transition-colors',
                isSelected && 'bg-accent/40 font-semibold'
              )}
            >
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
                {ws.label.toLowerCase().includes('personal') ? (
                  <UserCircle className="h-3 w-3" />
                ) : (
                  <Building2 className="h-3 w-3" />
                )}
              </div>
              <span className="flex-1 truncate">{ws.label}</span>
              {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
